/**
 * ChildIcon — Icône enfant genrée
 * Garçon : icône bébé standard (lucide Baby)
 * Fille  : même bébé avec de petits cheveux ajoutés sur la tête
 *
 * Usage : <ChildIcon sex="F" className="h-5 w-5" />
 */
import { cn } from '@/lib/utils';

// Girl baby SVG — same as lucide Baby but with hair strokes
const GirlBabySvg = ({ className, style, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24" height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    {...props}
  >
    {/* Hair — small curls on top of the head */}
    <path d="M8.5 5.5C8 3.5 9.5 2 12 2c2.5 0 4 1.5 3.5 3.5" strokeWidth="1.8" />
    <path d="M7.5 6C7 4.5 7.5 3 9 2.5" strokeWidth="1.5" />
    <path d="M16.5 6C17 4.5 16.5 3 15 2.5" strokeWidth="1.5" />
    {/* Head */}
    <path d="M9 12h.01" />
    <path d="M15 12h.01" />
    <path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" />
    <path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 1.1" />
  </svg>
);

// Boy baby — just use lucide Baby icon path
const BoyBabySvg = ({ className, style, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24" height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    {...props}
  >
    {/* Standard lucide Baby */}
    <path d="M9 12h.01" />
    <path d="M15 12h.01" />
    <path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" />
    <path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 1.1" />
  </svg>
);

/**
 * ChildIcon component
 * @param {string} sex - "F" for girl, "M" or anything else for boy
 * @param {string} className - Tailwind classes for sizing
 * @param {boolean} colored - if true, applies default gender colors (pink girl, teal boy)
 */
export const ChildIcon = ({ sex, className, colored = false, style, ...props }) => {
  const isGirl = sex === 'F';

  const genderStyle = colored
    ? { color: isGirl ? '#E8909C' : 'var(--primary-400)', ...style }
    : style;

  if (isGirl) {
    return <GirlBabySvg className={cn(className)} style={genderStyle} {...props} />;
  }
  return <BoyBabySvg className={cn(className)} style={genderStyle} {...props} />;
};

/** Gender-aware background color (for avatar circles, cards, etc.) */
export const genderBg = (sex) => sex === 'F' ? '#E8909C20' : 'var(--primary-100)';
export const genderColor = (sex) => sex === 'F' ? '#E8909C' : 'var(--primary-400)';
export const genderAccent = (sex) => sex === 'F' ? '#D47A86' : 'var(--primary-400)';
export const genderBorder = (sex) => sex === 'F' ? '#E8909C' : 'var(--primary-400)';
export const genderSelectedBg = (sex) => sex === 'F' ? 'bg-[#E8909C] hover:bg-[#D47A86] text-white border-[#E8909C]' : 'bg-[var(--primary-400)] hover:bg-[var(--primary-500)] text-white';
export const genderOutlineBg = (sex) => sex === 'F' ? 'border-[#E8909C]/50 text-[#D47A86] hover:bg-[#E8909C]/10 hover:border-[#E8909C]' : 'border-[var(--primary-400)]/50 text-[var(--primary-400)] hover:bg-[var(--primary-400)]/10 hover:border-[var(--primary-400)]';
